/*
  The hero vehicle finder's part filter, and where a finished search goes.

  Two jobs, kept together because they are two halves of one search: this
  builds the fourth "Part" row the app does not offer, and it points the
  finished search at the page that can act on it.

  The EasySearch app builds its own destination from the fitment data it was
  given, which is a collection URL: /collections/all/<vehicle-tag>. Search on
  this store runs through Searchanise on /pages/search-results-page, which
  filters on that same tag through its rb_tags parameter, so the button is
  pointed there instead.

  The app navigates by letting the browser follow the button's href once its
  inline onclick (easysearch.saveSearchParams) has returned. Rewriting the
  href from a capturing listener therefore changes the destination and
  nothing else: the app's handler still runs, and it still reads its tags
  from the holder's esparams rather than from the URL, so the cookies it
  keeps for the garage and for pre-selecting the widget stay correct.

  The results page comes from a data attribute on the widget wrapper, set by
  the Hero section. With no attribute, or a blank one, the app keeps its own
  destination.

  The part filter rides along as the search text. Nothing in the catalogue
  carries a product type, a category tag or a collection of its own -- there
  is one collection, "all" -- so the part words exist only in the product
  titles, and Searchanise's q parameter is the only thing that reads them. It
  composes with rb_tags: the vehicle narrows by tag, the part by text. Note
  that q is always AND -- Searchanise treats |, OR, commas and spaces alike
  and requires every word -- so a part term can only narrow a vehicle, never
  widen it. Only facet values OR, and only pipe-separated.
*/
(() => {
  /* ---------------------------------------------------------------
     Put the part select where the app would have put a fourth field.
     --------------------------------------------------------------- */

  // Moves the templated row into the app's own holder, immediately above the
  // search button. Returns false only while the app has yet to draw itself,
  // which is the caller's cue to keep waiting.
  const placePart = (widget) => {
    const template = widget.querySelector('template[data-fitment-part-template]');
    if (!template) return true;

    const holder = widget.querySelector('.easysearch-holder');
    if (!holder) return false;
    if (holder.querySelector('.hero-finder__part')) return true;

    const row = template.content.firstElementChild;
    if (!row) return true;

    // A null reference node appends, which is the right answer if the app
    // ever stops rendering an actions row.
    holder.insertBefore(row.cloneNode(true), holder.querySelector('.easysearch-actions-holder'));
    return true;
  };

  const watchWidget = (widget) => {
    if (widget.dataset.fitmentPartWatched) return;
    widget.dataset.fitmentPartWatched = 'true';

    placePart(widget);

    // Left running rather than disconnected on the first success: the app
    // redraws its holder when the garage switches or clears a vehicle, which
    // takes the row with it. placePart only writes when the row is absent,
    // so re-entry cannot loop.
    new MutationObserver(() => placePart(widget)).observe(widget, {
      childList: true,
      subtree: true,
    });
  };

  // Re-run on section load so the row survives edits in the theme editor.
  const init = () => document.querySelectorAll('.hero-finder__widget').forEach(watchWidget);
  init();
  document.addEventListener('shopify:section:load', init);

  /* ---------------------------------------------------------------
     Redirect the finished search.
     --------------------------------------------------------------- */

  // The tag the app matched. esparams is set by the app on every change and
  // is authoritative; the href can lag a selection behind, so it is only the
  // fallback. Multiple tags are joined with + when universal products are on.
  const tagsFor = (link, href) => {
    const holder = link.closest('.easysearch-holder');
    const fromApp = holder && holder.esparams && holder.esparams.tags;
    if (fromApp) return fromApp.split('+').filter(Boolean);

    const path = href.split('?')[0].replace(/\/+$/, '');
    const slug = path.slice(path.lastIndexOf('/') + 1);
    return slug ? [slug] : [];
  };

  document.addEventListener(
    'click',
    (event) => {
      const target = event.target;
      if (!target || !target.closest) return;

      const link = target.closest('a.easysearch-btn-search');
      if (!link || link.hasAttribute('disabled')) return;

      const scope = link.closest('[data-fitment-results-url]');
      const base = scope && scope.dataset.fitmentResultsUrl;
      if (!base) return;

      // Until the last select is answered the app parks the button on
      // javascript:void(0), which is not ours to redirect.
      const href = link.getAttribute('href') || '';
      if (href.charAt(0) !== '/') return;

      const tags = tagsFor(link, href);
      if (!tags.length) return;

      const url = new URL(base, window.location.origin);
      url.searchParams.set('page', '1');
      // One parameter, values joined with a pipe. Searchanise ORs the values
      // inside a facet, but only in that form: repeating the parameter makes
      // the last occurrence win and silently drops the rest, which would
      // lose every tag but one whenever the app matches several (its
      // universal-products case). This is the form the app's own sidebar
      // builds when two boxes are ticked.
      url.searchParams.set('rb_tags', tags.join('|'));

      // Blank is the "All parts" option, which searches the whole vehicle.
      const part = scope.querySelector('select[data-fitment-part]');
      const term = part ? part.value.trim() : '';
      url.searchParams.delete('q');
      if (term) url.searchParams.set('q', term);

      const destination = url.pathname + url.search;
      link.setAttribute('href', destination);

      // Autosubmit mode navigates from esparams instead of the href, and the
      // app stores this URL as the garage's last search either way.
      const holder = link.closest('.easysearch-holder');
      if (holder && holder.esparams) holder.esparams.url = destination;
    },
    true
  );
})();
